import { Injectable } from "@angular/core";
import { MVIQ_FLAG_SUFFIX, TAGS_FLAG_VALUE, TAGS_MAX_COUNT } from "../app.constants";
import { LeafLocation } from "../_offline/models/subjectModel-locations.schema";
import { firstValueFrom } from "rxjs";
import { TagsDbService } from "./database/tags-db.service";
import { NetworkStatusService } from "./network-status.service";

@Injectable({
  providedIn: "root"
})
export class MviqUtilsService {
  constructor(
    private networkStatusService: NetworkStatusService,
    private tagsDbService: TagsDbService
  ) {}

  hasMviqFlagSuffix(imageNameWithoutExtension: string): boolean {
    if (!imageNameWithoutExtension || imageNameWithoutExtension.trim() === "") {
      return false;
    }

    return imageNameWithoutExtension.endsWith(MVIQ_FLAG_SUFFIX);
  }

  getLocationIdentifier(isMviqImage: boolean, imageNameWithoutMimeType: string, leafLocations: LeafLocation[], defaultLocation: string): string {
    if (!isMviqImage || defaultLocation) {
      return defaultLocation;
    }

    // Remove '-FLAG' suffix if it exists
    if (imageNameWithoutMimeType.endsWith("-FLAG")) {
      imageNameWithoutMimeType = imageNameWithoutMimeType.slice(0, -5);
    }

    // Remove the last 3 characters if they are digits
    if (imageNameWithoutMimeType.length >= 3 && /\d{3}$/.test(imageNameWithoutMimeType)) {
      imageNameWithoutMimeType = imageNameWithoutMimeType.slice(0, -3);
    }

    // Find the first location where the name contains the modified imageName
    const matchingLocation = leafLocations.find((location) => location.name.includes(imageNameWithoutMimeType));
    return matchingLocation ? matchingLocation.identifier : "";
  }

  getLeafLocations(locationsJson: string): LeafLocation[] {
    const leafLocations: LeafLocation[] = [];
    if (!locationsJson) {
      return leafLocations;
    }
    let locations: Array<{ identifier: string; name: string; children: any[] }> = [];

    try {
      locations = JSON.parse(locationsJson);
    } catch (error) {
      console.error("Invalid JSON format for locations:", error);
      return leafLocations;
    }

    // Recursive function to get all leaf/final locations
    const findLeafLocations = (locations: any[]): void => {
      locations.forEach((location) => {
        if (location.children && location.children.length > 0) {
          // Recursive call to get leaves from children
          findLeafLocations(location.children);
        } else {
          // if no children, add name and identifier to leaf locations
          leafLocations.push({ name: location.name, identifier: location.identifier });
        }
      });
    };

    // Start the recursion with the parsed locations
    findLeafLocations(locations);

    return leafLocations;
  }

  async getMviqTagFlagIsAvailable(): Promise<boolean> {
    if (this.networkStatusService.isOnPremise) {
      return false;
    }

    try {
      const dbTags = await firstValueFrom(this.tagsDbService.getAll());

      let items: string[] = this.parseItems(dbTags?.items || "[]");

      if (items.includes(TAGS_FLAG_VALUE)) {
        return true;
      }

      if (items.length >= TAGS_MAX_COUNT) {
        return false;
      }

      items.push(TAGS_FLAG_VALUE);
      dbTags.items = JSON.stringify(items);

      const updatedTag = await firstValueFrom(this.tagsDbService.patch(dbTags));

      items = this.parseItems(updatedTag?.items || "[]");
      return items.includes(TAGS_FLAG_VALUE);
    } catch {
      return false;
    }
  }

  private parseItems(items: any): string[] {
    if (!items) {
      return [];
    }
    return typeof items === "string" ? JSON.parse(items) : items;
  }
}
